// Tiny i18n for the phone-menu guide only (the rest of the app is English).
// Language is remembered per browser; the default follows the UI language.

import { useCallback, useEffect, useState } from 'react';
import { en, type I18nKey } from './en';
import { nl } from './nl';

export type Lang = 'nl' | 'en';
export type { I18nKey };

const DICTS: Record<Lang, Record<I18nKey, string>> = { en, nl };
const STORAGE_KEY = 'qd.lang';

export function translate(lang: Lang, key: I18nKey, vars?: Record<string, string | number>): string {
  let s: string = DICTS[lang][key] ?? en[key] ?? key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}

export function detectLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'nl' || stored === 'en') return stored;
  } catch { /* storage may be blocked */ }
  const nav = typeof navigator !== 'undefined' ? (navigator.language || '') : '';
  return nav.toLowerCase().startsWith('nl') ? 'nl' : 'en';
}

export function useLang(): [Lang, (l: Lang) => void, (key: I18nKey, vars?: Record<string, string | number>) => string] {
  const [lang, setLangState] = useState<Lang>(() => detectLang());
  useEffect(() => { try { window.localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ } }, [lang]);
  const t = useCallback((key: I18nKey, vars?: Record<string, string | number>) => translate(lang, key, vars), [lang]);
  const setLang = useCallback((l: Lang) => setLangState(l), []);
  return [lang, setLang, t];
}

/** Localized weekday helpers for the status/hours helpers. */
export function dayShort(lang: Lang) {
  return (d: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun') => translate(lang, d);
}
export function dayLong(lang: Lang, d: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'): string {
  const map = { mon: 'monday', tue: 'tuesday', wed: 'wednesday', thu: 'thursday', fri: 'friday', sat: 'saturday', sun: 'sunday' } as const;
  return translate(lang, map[d]);
}
